import React, { useEffect, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";

interface MemoryModalProps {
  open: boolean;
  onClose: () => void;
  conversationId?: string;
}

/* ────────────────────────────────────────────────────────── */
/* helpers                                                    */
/* ────────────────────────────────────────────────────────── */
const prettyDate = (d?: string) =>
  d ? format(new Date(d), "yyyy-MM-dd HH:mm:ss") : "";

const Field = ({
  label,
  value,
}: {
  label: string;
  value: any;
}): JSX.Element | null =>
  value !== undefined && value !== null && value !== "" ? (
    <Typography variant="body2">
      <strong>{label}:</strong> {value}
    </Typography>
  ) : null;

const CardRow: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

/* ────────────────────────────────────────────────────────── */
/* component                                                  */
/* ────────────────────────────────────────────────────────── */
const MemoryModal: React.FC<MemoryModalProps> = ({
  open,
  onClose,
  conversationId,
}) => {
  /* — redux state — */
  const memoryData: any = useSelector((state: RootState) =>
    conversationId
      ? state.conversations.memoryData?.[conversationId]?.data
      : undefined
  );
  const memoryVersions: Record<string, string> = useSelector(
    (state: RootState) =>
      conversationId
        ? state.conversations.memoryData?.[conversationId]
            ?.memory_state_versions
        : {}
  );
  const loadingMemory = useSelector((state: RootState) =>
    conversationId
      ? state.conversations.loadingMemoryData?.[conversationId]
      : false
  );

  /* — refs / handlers — */
  const [tab, setTab] = React.useState<
    "sessions" | "human" | "people" | "events"
  >("sessions");
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open && modalRef.current) modalRef.current.focus();
  }, [open]);

  const blockCtrlF = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "f") e.stopPropagation();
  };

  /* — partitions — */
  const sessions = memoryData?.background?.progress?.records ?? {};
  const human = memoryData?.background?.human ?? {};
  const people = memoryData?.people ?? {};
  const owner = people?.owner;
  const peopleEntries = people?.people ?? {};
  const events = memoryData?.events?.events ?? {};

  /* — loading / empty states — */
  if (!conversationId || loadingMemory) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Memory Viewer
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading…</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!memoryData) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Memory Viewer
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>No memory data available.</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  /* — main render — */
  return (
    <Dialog
      ref={modalRef}
      onKeyDown={blockCtrlF}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Memory Viewer
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ───────────── CONTENT ───────────── */}
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: "flex", height: "72vh" }}>
          {/* — left rail — */}
          <Tabs
            orientation="vertical"
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderRight: 1,
              borderColor: "divider",
              minWidth: 210,
              "& .MuiTab-root": { alignItems: "flex-start" },
            }}
          >
            <Tab label="Sessions & Topics" value="sessions" />
            <Tab label="Human" value="human" />
            <Tab label="People" value="people" />
            <Tab label="Events" value="events" />
          </Tabs>

          {/* — content pane — */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 3,
              bgcolor: "background.default",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* │──────────────────── active tab content ────────────────────│ */}
            <Box sx={{ flex: 1 }}>
              {/* — TAB: Sessions & Topics — */}
              {tab === "sessions" && (
                <>
                  {Object.keys(sessions).length === 0 ? (
                    <Typography>No session summaries found.</Typography>
                  ) : (
                    Object.entries(sessions)
                      .sort(
                        ([, a]: any, [, b]: any) =>
                          new Date(b.updated_at).getTime() -
                          new Date(a.updated_at).getTime()
                      )
                      .map(([key, s]: any) => (
                        <CardRow
                          key={key}
                          title={
                            s.updated_at
                              ? `Session • ${prettyDate(s.updated_at)}`
                              : "Session"
                          }
                        >
                          <Stack spacing={1}>
                            {Field({ label: "Summary", value: s.summary })}
                            {s.has_multiple_topics && (
                              <Typography variant="body2">
                                <strong>Multiple Topics:</strong> Yes
                              </Typography>
                            )}

                            {/* topic threads */}
                            {s.topic_threads &&
                            Object.keys(s.topic_threads).length ? (
                              <Accordion sx={{ mt: 1 }}>
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                >
                                  <Typography>Topic Threads</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  {Object.values(s.topic_threads).map(
                                    (t: any, idx: number) => (
                                      <Card
                                        key={idx}
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                      >
                                        <CardContent sx={{ p: 1.5 }}>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: "bold" }}
                                          >
                                            {t.title}
                                          </Typography>
                                          {Field({
                                            label: "Summary",
                                            value: t.summary,
                                          })}
                                          {Field({
                                            label: "Updated",
                                            value: prettyDate(t.updated_at),
                                          })}
                                        </CardContent>
                                      </Card>
                                    )
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            ) : null}
                          </Stack>
                        </CardRow>
                      ))
                  )}
                </>
              )}

              {/* — TAB: Human — */}
              {tab === "human" && (
                <>
                  <CardRow title="Human">
                    {Field({
                      label: "Intro Summary",
                      value: human.intro_summary,
                    })}
                    {Field({
                      label: "Background Summary",
                      value: human.background_summary,
                    })}
                  </CardRow>
                </>
              )}

              {/* — TAB: People — */}
              {tab === "people" && (
                <>
                  {owner && (
                    <CardRow title="Owner">
                      <Stack spacing={1}>
                        {Field({ label: "Name", value: owner.name })}
                        {Field({ label: "Summary", value: owner.summary })}
                        {Field({
                          label: "Last Discussion",
                          value: prettyDate(owner.last_discussion_time),
                        })}

                        {/* demographics */}
                        {(owner.demographics?.lives_in ||
                          owner.demographics?.age ||
                          owner.demographics?.originally_from ||
                          owner.demographics?.employer) && (
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>Demographics</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {Field({
                                label: "Lives In",
                                value: owner.demographics?.lives_in,
                              })}
                              {Field({
                                label: "Age",
                                value: owner.demographics?.age,
                              })}
                              {Field({
                                label: "Originally From",
                                value: owner.demographics?.originally_from,
                              })}
                              {Field({
                                label: "Employer",
                                value: owner.demographics?.employer,
                              })}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Stack>
                    </CardRow>
                  )}

                  {/* divider to separate others */}
                  {owner && (
                    <Divider sx={{ my: 3 }}>
                      <Typography variant="subtitle2">
                        People&nbsp;in&nbsp;Memory
                      </Typography>
                    </Divider>
                  )}

                  {Object.keys(peopleEntries).length === 0 ? (
                    <Typography>No people records found.</Typography>
                  ) : (
                    Object.entries(peopleEntries).map(([key, p]: any) => (
                      <CardRow key={key} title={p.name || "Person"}>
                        <Stack spacing={1}>
                          {Field({ label: "Email", value: p.email })}
                          {Field({
                            label: "Relationship",
                            value: p.relationship,
                          })}
                          {Field({
                            label: "Knows Personally",
                            value: p.knows_personally ? "Yes" : "",
                          })}
                          {Field({ label: "Summary", value: p.summary })}
                          {Field({
                            label: "Updated At",
                            value: prettyDate(p.updated_at),
                          })}

                          {/* demographics */}
                          {(p.demographics?.lives_in ||
                            p.demographics?.age ||
                            p.demographics?.originally_from ||
                            p.demographics?.employer) && (
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Demographics</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                {Field({
                                  label: "Lives In",
                                  value: p.demographics?.lives_in,
                                })}
                                {Field({
                                  label: "Age",
                                  value: p.demographics?.age,
                                })}
                                {Field({
                                  label: "Originally From",
                                  value: p.demographics?.originally_from,
                                })}
                                {Field({
                                  label: "Employer",
                                  value: p.demographics?.employer,
                                })}
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {/* bot discussions */}
                          {p.bot_discussions &&
                          Object.keys(p.bot_discussions).length ? (
                            <Accordion sx={{ mt: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Bot Discussions</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                {Object.values(p.bot_discussions).map(
                                  (d: any, ix: number) => (
                                    <Box key={ix} sx={{ mb: 1 }}>
                                      {Field({
                                        label: "Chat Session ID",
                                        value: d.chat_session_id,
                                      })}
                                      {Field({
                                        label: "Summary",
                                        value: d.summary,
                                      })}
                                      {Field({
                                        label: "Updated At",
                                        value: prettyDate(d.updated_at),
                                      })}
                                    </Box>
                                  )
                                )}
                              </AccordionDetails>
                            </Accordion>
                          ) : null}
                        </Stack>
                      </CardRow>
                    ))
                  )}
                </>
              )}

              {/* — TAB: Events — */}
              {tab === "events" && (
                <>
                  {Object.keys(events).length === 0 ? (
                    <Typography>No events found.</Typography>
                  ) : (
                    Object.entries(events)
                      .sort(
                        ([, a]: any, [, b]: any) =>
                          new Date(a.estimated_date).getTime() -
                          new Date(b.estimated_date).getTime()
                      )
                      .map(([key, e]: any) => (
                        <CardRow key={key} title={e.name}>
                          <Stack spacing={1}>
                            {Field({
                              label: "Description",
                              value: e.description,
                            })}
                            {Field({
                              label: "Estimated Date",
                              value: prettyDate(e.estimated_date),
                            })}
                            {Field({
                              label: "Updated At",
                              value: prettyDate(e.updated_at),
                            })}
                            {e.ok_to_bring_up !== undefined &&
                              Field({
                                label: "Ok to Bring Up",
                                value: e.ok_to_bring_up ? "Yes" : "No",
                              })}
                          </Stack>
                        </CardRow>
                      ))
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* versions — bottom-left */}
        {Object.keys(memoryVersions).length > 0 && (
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
            {Object.entries(memoryVersions).map(([k, v]) => (
              <Typography key={k} variant="caption">
                <strong>{k}:</strong> v{v}
              </Typography>
            ))}
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MemoryModal;
